import { formatArticleDate } from '@archetelos/lib/date';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';

import { useAuth } from '@/lib/auth/useAuth';
import { creatorApplicationQueries } from '@/lib/queries/creatorApplication';

export const Route = createFileRoute('/creator/application-status')({
  component: ApplicationStatusRoute,
});

function ApplicationStatusRoute() {
  const { user, profile } = useAuth();
  const application = useQuery(creatorApplicationQueries.latestForUser(user?.id));

  return (
    <div className="mx-auto max-w-2xl">
      <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">
        Application status
      </p>
      <h1 className="mt-2 font-serif text-3xl text-ink sm:text-4xl">
        Your contributor application
      </h1>

      {profile?.creator_status === 'pending' && (
        <div className="mt-8 rounded-md border border-ink/10 bg-parchment p-6">
          <p className="font-sans text-sm text-ink/80 pretty">
            Editorial is reviewing your application. We&apos;ll email you when a decision is made —
            typically within a week. You don&apos;t need to do anything in the meantime.
          </p>
          {application.data && (
            <p className="mt-4 font-sans text-xs uppercase tracking-[0.12em] text-ink/50">
              Submitted {formatArticleDate(new Date(application.data.created_at))}
            </p>
          )}
        </div>
      )}

      {profile?.creator_status === 'rejected' && application.data && (
        <div className="mt-8 rounded-md border border-oxblood/30 bg-oxblood/5 p-6">
          <p className="font-sans text-xs uppercase tracking-[0.12em] text-oxblood">
            Application declined ·{' '}
            {application.data.reviewed_at &&
              formatArticleDate(new Date(application.data.reviewed_at))}
          </p>
          {application.data.reviewer_notes && (
            <p className="mt-3 font-sans text-sm text-ink/80 pretty">
              {application.data.reviewer_notes}
            </p>
          )}
          <p className="mt-4 font-sans text-sm text-ink/70 pretty">
            You&apos;re welcome to read and watch as a subscriber. You can also reapply with new
            sample work at any time — editorial appreciates substantive revisions.
          </p>
          <Link
            to="/onboarding/creator"
            className="mt-5 inline-block rounded-md bg-ink px-5 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
          >
            Reapply with new work →
          </Link>
        </div>
      )}

      {(!profile?.creator_status || profile.creator_status === 'none') && (
        <div className="mt-8 rounded-md border border-ink/10 bg-parchment p-6">
          <p className="font-sans text-sm text-ink/80 pretty">
            You haven&apos;t applied yet. Contributors publish essays and video across the
            traditions — applications are reviewed by editorial within a week.
          </p>
          <Link
            to="/onboarding/creator"
            className="mt-5 inline-block rounded-md bg-ink px-5 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
          >
            Apply →
          </Link>
        </div>
      )}

      <p className="mt-12 text-center text-sm text-ink/60">
        Browse the publication while you wait —{' '}
        <Link to="/subscriber" className="text-oxblood hover:underline">
          go to your home
        </Link>
        .
      </p>
    </div>
  );
}
